<template>
  <div class="h-full flex flex-col min-h-0 bg-[#fafbfc] relative overflow-hidden min-w-0">
    <header class="px-8 py-6 sticky top-0 z-30 bg-[#fafbfc]/90 backdrop-blur-md border-b border-[#1c4848]/5 shrink-0">
      <div class="max-w-[1600px] mx-auto">
        <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#1c4848]/40">Dashboard</span>
        <h1 class="text-2xl font-serif font-medium text-[#1c4848] tracking-tight">Users</h1>
      </div>
    </header>

    <main class="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-8 pt-6 pb-32">
      <div class="max-w-[1600px] mx-auto space-y-5">
        <div class="grid grid-cols-2 gap-3 sm:max-w-xl" role="group" aria-label="Which users to show">
          <button v-for="tile in tiles" :key="tile.key" type="button" class="rounded-2xl px-4 py-3 text-left transition-all" :class="kind === tile.key ? 'bg-[#1c4848] text-white shadow-md' : 'bg-white border border-[#1c4848]/10 text-[#1c4848] hover:border-[#1c4848]/30'" :aria-pressed="kind === tile.key" @click="choose(tile.key)">
            <span class="block text-sm font-bold">{{ tile.label }}</span>
            <span class="block text-xs tabular-nums" :class="kind === tile.key ? 'text-white/70' : 'text-[#1c4848]/55'">{{ tile.count }}</span>
          </button>
        </div>

        <input v-model="search" type="search" :placeholder="kind === 'accounts' ? 'Search by name or e-mail' : 'Search by phone, country code, account number or device ID'" :aria-label="kind === 'accounts' ? 'Search by name or e-mail' : 'Search phones'" class="w-full max-w-xl rounded-xl border border-[#1c4848]/10 bg-white px-4 py-2.5 text-sm text-[#1c4848] placeholder:text-[#1c4848]/35 focus:border-[#1c4848]/40 focus:outline-none">

        <p v-if="store.error" role="alert" class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {{ store.error }}
          <button type="button" class="ms-2 underline" @click="load">Try again</button>
        </p>

        <section v-else class="overflow-hidden rounded-3xl border border-[#1c4848]/8 bg-white shadow-sm" :aria-busy="store.loading">
          <div v-if="store.loading && !store.rows.length" class="space-y-3 p-6"><div v-for="i in 6" :key="i" class="h-12 animate-pulse rounded-2xl bg-[#1c4848]/5"></div></div>
          <p v-else-if="!store.rows.length" class="px-6 py-14 text-center text-sm text-[#1c4848]/50">{{ search.trim() ? `Nobody matches "${search.trim()}".` : (kind === 'accounts' ? 'No Academy accounts yet.' : 'No phones yet.') }}</p>
          <ul v-else class="divide-y divide-[#1c4848]/5" :class="store.loading ? 'opacity-60' : ''">
            <li v-for="user in store.rows" :key="user.id">
              <button type="button" class="flex min-h-[64px] w-full items-center gap-4 px-5 py-3 text-left hover:bg-[#1c4848]/[0.03]" @click="openId = user.id">
                <UserPhoto :user="user" />
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-semibold text-[#1c4848]">{{ user.name }}</span>
                  <span class="block truncate text-xs text-[#1c4848]/55">{{ lineUnderName(user) }}</span>
                </span>
                <span v-if="user.premium" class="shrink-0 rounded-full bg-[#ecd79f]/50 px-2.5 py-1 text-[11px] font-bold text-[#7a5c14]">Premium</span>
                <span v-for="label in user.role_labels" :key="label" class="hidden shrink-0 rounded-full bg-[#1c4848]/10 px-2.5 py-1 text-[11px] font-bold text-[#1c4848] md:inline">{{ label }}</span>
                <span v-if="testLabel(user)" class="hidden shrink-0 rounded-full bg-[#fff7e0] px-2.5 py-1 text-[11px] font-bold text-[#8a5a00] md:inline">{{ testLabel(user) }}</span>
                <span class="hidden w-32 shrink-0 text-right text-xs tabular-nums text-[#1c4848]/55 sm:block">{{ timeLabel(user) }}</span>
              </button>
            </li>
          </ul>
        </section>

        <nav v-if="store.meta.pages > 1 && !store.error" class="flex items-center justify-between gap-4" aria-label="Pages">
          <button type="button" class="min-h-11 rounded-xl border border-[#1c4848]/10 bg-white px-4 text-sm font-bold text-[#1c4848] disabled:opacity-40" :disabled="page <= 1" @click="page -= 1">Previous</button>
          <span class="text-xs tabular-nums text-[#1c4848]/55">Page {{ store.meta.page }} of {{ store.meta.pages }}</span>
          <button type="button" class="min-h-11 rounded-xl border border-[#1c4848]/10 bg-white px-4 text-sm font-bold text-[#1c4848] disabled:opacity-40" :disabled="page >= store.meta.pages" @click="page += 1">Next</button>
        </nav>
      </div>
    </main>

    <UserPanel v-if="openUser" :key="openUser.id" :user="openUser" @close="openId = null" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import UserPanel from '@/components/app/dashboard/users/UserPanel.vue';
import UserPhoto from '@/components/app/dashboard/users/UserPhoto.vue';
import { useUsersStore } from '@/store/users/index.js';
import { regionName } from '@/utils/regionName.js';

const TEST_BUILDS = { testflight: 'TestFlight', debug: 'Xcode', simulator: 'Simulator' };

const store = useUsersStore();
const kind = ref('accounts');
const search = ref('');
const page = ref(1);
const openId = ref(null);
let timer = null;

const openUser = computed(() => store.rows.find((user) => user.id === openId.value) ?? null);
const count = (value) => (value === null ? '…' : value.toLocaleString('en-GB'));
const tiles = computed(() => [
  { key: 'accounts', label: 'Academy accounts', count: count(store.meta.accounts) },
  { key: 'devices', label: 'App devices', count: count(store.meta.devices) },
]);

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function lineUnderName(user) {
  if (user.kind === 'accounts') return user.email;
  return [user.device_model, user.region && regionName(user.region), user.app_version && `v${user.app_version}`].filter(Boolean).join(' · ') || 'Older app build';
}
const testLabel = (user) => TEST_BUILDS[user.environment] ?? '';
function timeLabel(user) {
  if (user.kind === 'devices') return user.seen_at ? `Seen ${dateFormat.format(new Date(user.seen_at))}` : '';
  return user.joined_at ? `Joined ${dateFormat.format(new Date(user.joined_at))}` : '';
}

function load() {
  return store.fetch({ kind: kind.value, search: search.value.trim(), page: page.value });
}

function choose(next) {
  if (next === kind.value) return;
  kind.value = next;
  search.value = '';
  page.value = 1;
  openId.value = null;
  clearTimeout(timer);
  load();
}

watch(page, load);
watch(search, () => {
  clearTimeout(timer);
  timer = setTimeout(() => {
    if (page.value === 1) load();
    else page.value = 1;
  }, 300);
});

onMounted(load);
onBeforeUnmount(() => clearTimeout(timer));
</script>
